import React, { useEffect, useRef, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import {
  CUSTOMER_ROLE_OPTIONS,
  getRoleLabel,
  isCustomerAssignableRole,
  KNOWN_ROLES
} from '../../utils/rolePermissions';
import {
  inviteTenantUser,
  listOrganizations,
  listTenantMemberships,
  saveOrganization,
  updateMembership
} from '../../services/TenantAdminService';

const ORG_DRAFT_KEY = 'saleswork_tenant_admin_org_draft';
const EMPLOYEE_DRAFT_KEY = 'saleswork_tenant_admin_employee_draft';
const SELECTED_TENANT_KEY = 'saleswork_tenant_admin_selected_tenant';

const emptyOrganization = {
  name: '',
  slug: '',
  website: '',
  contact_email: '',
  status: 'active',
  plan: 'enterprise'
};

const readDraft = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const saveDraft = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Keep the form usable if browser storage is unavailable.
  }
};

const clearDraft = key => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore browser storage failures.
  }
};

const TenantAdminView = ({ onBack }) => {
  const [organizations, setOrganizations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [organization, setOrganization] = useState(emptyOrganization);
  const [employee, setEmployee] = useState({ name: '', email: '', role: 'Sales' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const restoredDrafts = useRef(false);
  const organizationDraftRestorePending = useRef(false);
  const employeeDraftRestorePending = useRef(false);

  const loadOrganizations = async () => {
    setLoading(true);
    const result = await listOrganizations();
    setLoading(false);

    if (!result.success) {
      setMessage(result.error?.message || 'Kunne ikke hente organisasjoner.');
      return;
    }

    setOrganizations(result.data);

    const selectedTenantId = readDraft(SELECTED_TENANT_KEY, null);
    const organizationDraft = readDraft(ORG_DRAFT_KEY, null);
    const restoredTenant = result.data.find(item => item.id === selectedTenantId);

    if (restoredTenant) {
      setSelected(restoredTenant);

      if (!organizationDraft) {
        setOrganization(restoredTenant);
      }

      if (organizationDraft) {
        setOrganization(organizationDraft);
      }

      const membershipsResult = await listTenantMemberships(restoredTenant.id);
      if (membershipsResult.success) {
        setMemberships(membershipsResult.data);
      } else {
        setMessage(membershipsResult.error?.message || 'Kunne ikke hente ansatte.');
      }
    } else if (selectedTenantId) {
      clearDraft(SELECTED_TENANT_KEY);
      setSelected(null);
      setMemberships([]);
    }
  };

  const openOrganization = async item => {
    clearDraft(ORG_DRAFT_KEY);
    setSelected(item);
    saveDraft(SELECTED_TENANT_KEY, item.id);
    setOrganization(item);
    setMessage('');

    const result = await listTenantMemberships(item.id);
    if (result.success) {
      setMemberships(result.data);
    } else {
      setMessage(result.error?.message || 'Kunne ikke hente ansatte.');
    }
  };

  useEffect(() => {
    const organizationDraft = readDraft(ORG_DRAFT_KEY, null);
    const employeeDraft = readDraft(EMPLOYEE_DRAFT_KEY, null);

    if (organizationDraft) {
      organizationDraftRestorePending.current = true;
      setOrganization(organizationDraft);
    }

    if (employeeDraft) {
      employeeDraftRestorePending.current = true;
      setEmployee(employeeDraft);
    }

    restoredDrafts.current = true;
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (restoredDrafts.current) {
      if (organizationDraftRestorePending.current) {
        organizationDraftRestorePending.current = false;
        return;
      }

      saveDraft(ORG_DRAFT_KEY, organization);
    }
  }, [organization]);

  useEffect(() => {
    if (restoredDrafts.current) {
      if (employeeDraftRestorePending.current) {
        employeeDraftRestorePending.current = false;
        return;
      }

      saveDraft(EMPLOYEE_DRAFT_KEY, employee);
    }
  }, [employee]);

  const submitOrganization = async event => {
    event.preventDefault();
    setSaving(true);
    setMessage('');

    const result = await saveOrganization(organization);
    setSaving(false);

    if (!result.success) {
      setMessage(result.error?.message || 'Kunne ikke lagre organisasjonen.');
      return;
    }

    clearDraft(ORG_DRAFT_KEY);
    saveDraft(SELECTED_TENANT_KEY, result.data.id);

    setOrganizations(current => {
      const exists = current.some(item => item.id === result.data.id);
      return exists
        ? current.map(item => item.id === result.data.id ? result.data : item)
        : [...current, result.data].sort((a, b) => a.name.localeCompare(b.name));
    });
    setSelected(result.data);
    setOrganization(result.data);
    setMessage('Organisasjonen er lagret.');
  };

  const submitEmployee = async event => {
    event.preventDefault();
    if (!selected) return;

    setSaving(true);
    setMessage('');

    const result = await inviteTenantUser({
      tenant_id: selected.id,
      name: employee.name,
      email: employee.email,
      role: employee.role
    });

    setSaving(false);

    if (!result.success) {
      setMessage(result.error?.message || 'Kunne ikke koble ansatt til organisasjonen.');
      return;
    }

    clearDraft(EMPLOYEE_DRAFT_KEY);

    if (result.warning) {
      setMessage(result.warning);
    } else {
      setMessage(`${result.profile?.email || employee.email} er koblet til ${selected.name}.`);
      const membershipsResult = await listTenantMemberships(selected.id);
      if (membershipsResult.success) setMemberships(membershipsResult.data);
    }

    setEmployee({ name: '', email: '', role: 'Sales' });
  };

  const changeMembership = async (membership, changes) => {
    const result = await updateMembership(membership.id, changes);

    if (!result.success) {
      setMessage(result.error?.message || 'Kunne ikke oppdatere medlemskapet.');
      return;
    }

    setMemberships(current => current.map(item =>
      item.id === membership.id ? { ...item, ...result.data } : item
    ));
    setMessage('Medlemskapet er oppdatert.');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="bg-white px-8 py-6 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-slate-400 hover:text-slate-900">
            <SafeIcon icon="⬅️" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Organisasjoner og ansatte</h1>
            <p className="text-xs text-slate-500">Administrer plattformens tenants og memberships</p>
          </div>
        </div>
        <button
          onClick={loadOrganizations}
          className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold"
        >
          Oppdater
        </button>
      </div>

      <div className="max-w-7xl mx-auto mt-8 px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="space-y-4">
          <form onSubmit={submitOrganization} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h2 className="font-bold">{organization.id ? 'Rediger organisasjon' : 'Ny organisasjon'}</h2>
            <input
              required
              value={organization.name}
              onChange={event => setOrganization({ ...organization, name: event.target.value })}
              placeholder="Navn, f.eks. HMS Varsling"
              className="w-full mt-4 p-3 border border-slate-200 rounded-xl text-sm"
            />
            <input
              required
              value={organization.slug}
              onChange={event => setOrganization({ ...organization, slug: event.target.value })}
              placeholder="Slug, f.eks. hms-varsling"
              className="w-full mt-3 p-3 border border-slate-200 rounded-xl text-sm"
            />
            <input
              value={organization.website || ''}
              onChange={event => setOrganization({ ...organization, website: event.target.value })}
              placeholder="https://www.hmsvarsling.no"
              className="w-full mt-3 p-3 border border-slate-200 rounded-xl text-sm"
            />
            <input
              type="email"
              value={organization.contact_email || ''}
              onChange={event => setOrganization({ ...organization, contact_email: event.target.value })}
              placeholder="kontakt@bedrift.no"
              className="w-full mt-3 p-3 border border-slate-200 rounded-xl text-sm"
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <select
                value={organization.status || 'active'}
                onChange={event => setOrganization({ ...organization, status: event.target.value })}
                className="p-3 border border-slate-200 rounded-xl text-sm bg-white"
              >
                <option value="active">Aktiv</option>
                <option value="inactive">Inaktiv</option>
              </select>
              <select
                value={organization.plan || 'enterprise'}
                onChange={event => setOrganization({ ...organization, plan: event.target.value })}
                className="p-3 border border-slate-200 rounded-xl text-sm bg-white"
              >
                <option value="internal">Internal</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <button disabled={saving} className="w-full mt-4 py-3 rounded-xl bg-blue-600 text-white text-sm font-bold disabled:opacity-60">
              {saving ? 'Lagrer...' : 'Lagre organisasjon'}
            </button>
            <button
              type="button"
              onClick={() => {
                clearDraft(ORG_DRAFT_KEY);
                clearDraft(SELECTED_TENANT_KEY);
                setOrganization(emptyOrganization);
                setSelected(null);
                setMemberships([]);
              }}
              className="w-full mt-2 py-3 rounded-xl border border-slate-200 text-sm font-bold"
            >
              Ny organisasjon
            </button>
          </form>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="font-bold">Tenants</h2>
            </div>
            {loading ? (
              <p className="p-6 text-sm text-slate-500">Laster tenants...</p>
            ) : organizations.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">Ingen organisasjoner funnet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {organizations.map(item => (
                  <button
                    key={item.id}
                    onClick={() => openOrganization(item)}
                    className={`w-full text-left px-5 py-4 hover:bg-slate-50 ${selected?.id === item.id ? 'bg-blue-50' : ''}`}
                  >
                    <p className="font-bold">{item.name}</p>
                    <p className="text-xs text-slate-500">{item.slug} · {item.plan}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="lg:col-span-2 space-y-6">
          <form onSubmit={submitEmployee} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <h2 className="font-bold">Legg til ansatt</h2>
            <p className="text-xs text-slate-500 mt-1">
              Velg en eksisterende profil, eller registrer en invitasjon hvis brukeren ikke finnes ennå.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
              <input
                value={employee.name}
                onChange={event => setEmployee({ ...employee, name: event.target.value })}
                placeholder="Navn ved invitasjon"
                className="p-3 border border-slate-200 rounded-xl text-sm"
              />
              <input
                required
                type="email"
                value={employee.email}
                onChange={event => setEmployee({ ...employee, email: event.target.value })}
                placeholder="ansatt@bedrift.no"
                className="p-3 border border-slate-200 rounded-xl text-sm"
              />
              <select
                value={employee.role}
                onChange={event => setEmployee({ ...employee, role: event.target.value })}
                className="p-3 border border-slate-200 rounded-xl text-sm bg-white"
              >
                {CUSTOMER_ROLE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <button
              disabled={!selected || saving}
              className="mt-4 px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-bold disabled:opacity-50"
            >
              {saving ? 'Lagrer...' : selected ? `Koble til ${selected.name}` : 'Velg en tenant først'}
            </button>
          </form>

          {message && <p className="text-sm font-medium text-blue-700">{message}</p>}

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-bold">{selected ? `Ansatte i ${selected.name}` : 'Velg organisasjon'}</h2>
            </div>
            {!selected ? (
              <p className="p-8 text-sm text-slate-500">Velg en tenant for å se memberships.</p>
            ) : memberships.length === 0 ? (
              <p className="p-8 text-sm text-slate-500">Ingen aktive memberships funnet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {memberships.map(membership => {
                  const displayName = membership.profile?.name || membership.invite?.name;
                  const displayEmail = membership.profile?.email || membership.invite?.email;
                  const hasKnownRole = Boolean(
                    membership.role && KNOWN_ROLES.includes(membership.role)
                  );
                  const hasUnknownRole = Boolean(
                    membership.role && !hasKnownRole
                  );
                  const isSystemRole = membership.role === 'SuperAdmin' || membership.role === 'PlatformAdmin';

                  return (
                    <div key={membership.id} className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div>
                        {displayName || displayEmail ? (
                          <>
                            <p className="font-bold">{displayName || displayEmail}</p>
                            {displayEmail && displayName && (
                              <p className="text-xs text-slate-500">{displayEmail}</p>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="font-bold">Profil mangler</p>
                            <p className="text-xs text-slate-500">{membership.user_id}</p>
                            <p className="text-xs text-amber-700 mt-1">
                              Brukeren er koblet til tenant, men profil mangler. Brukeren kan logge inn hvis Auth-kontoen finnes.
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isSystemRole ? (
                          <span className="px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700">
                            {getRoleLabel(membership.role)}
                          </span>
                        ) : (
                          <select
                            value={membership.role}
                            onChange={event => changeMembership(membership, { role: event.target.value })}
                            className="p-2 border border-slate-200 rounded-lg text-xs bg-white"
                          >
                            {hasUnknownRole && (
                              <option value={membership.role}>
                                Ukjent rolle: {membership.role}
                              </option>
                            )}
                            {hasKnownRole && !isCustomerAssignableRole(membership.role) && (
                              <option value={membership.role}>
                                {getRoleLabel(membership.role)}
                              </option>
                            )}
                            {CUSTOMER_ROLE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        )}
                        {isSystemRole ? (
                          <span className="px-2 py-1 rounded-md border border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700">
                            {membership.status === 'active'
                              ? 'Aktiv'
                              : membership.status === 'suspended'
                                ? 'Suspendert'
                                : membership.status === 'cancelled'
                                  ? 'Kansellert'
                                  : membership.status}
                          </span>
                        ) : (
                          <select
                            value={membership.status}
                            onChange={event => changeMembership(membership, { status: event.target.value })}
                            className="p-2 border border-slate-200 rounded-lg text-xs bg-white"
                          >
                            <option value="active">Aktiv</option>
                            <option value="suspended">Suspendert</option>
                            <option value="cancelled">Kansellert</option>
                          </select>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TenantAdminView;